import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, ChevronDown, MoreVertical, User, Ban } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ChatBubble } from './ChatBubble';
import ChatInputToolbar from './ChatInputToolbar';

const API = '/api';

const FloatingChatWindow = ({ contact, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conversationId, setConversationId] = useState(contact.conversation_id || null);
  const [minimized, setMinimized] = useState(false);

  // Image attachment state
  const [pendingImage, setPendingImage] = useState(null); // File object
  const [imagePreview, setImagePreview] = useState(null); // data URL
  const [imageUploading, setImageUploading] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const shouldAutoScroll = useRef(true);
  const lastMarkReadRef = useRef(0);

  // Smart scroll
  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    if (force || shouldAutoScroll.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    shouldAutoScroll.current = isNearBottom();
  }, [isNearBottom]);

  // Persist reaction via API
  const handleReaction = useCallback(async (messageId, reactionType) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/messages/${messageId}/react`,
        { reaction_type: reactionType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Update the message's reactions in local state from server response
      const newReactions = res.data.reactions || [];
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, reactions: newReactions } : m)),
      );
    } catch {
      toast.error('Failed to react');
    }
  }, []);

  // Image selection handler
  const handleImageSelect = useCallback((file) => {
    setPendingImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleImageRemove = useCallback(() => {
    setPendingImage(null);
    setImagePreview(null);
  }, []);

  // Upload image to server
  const uploadImage = useCallback(async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    setImageUploading(true);
    try {
      const res = await axios.post(`${API}/messages/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      return res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
      return null;
    } finally {
      setImageUploading(false);
    }
  }, []);

  // Fetch conversation + messages
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        let convId = conversationId;
        if (!convId) {
          const res = await axios.post(
            `${API}/conversations/with`,
            { other_user_id: contact.user_id },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          convId = res.data.conversation_id;
          if (!cancelled) setConversationId(convId);
        }
        const msgRes = await axios.get(`${API}/messages/${convId}?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setMessages(Array.isArray(msgRes.data) ? msgRes.data : []);
          // Mark-read on initial load only
          const now = Date.now();
          if (now - lastMarkReadRef.current > 3000) {
            lastMarkReadRef.current = now;
            axios
              .patch(`${API}/messages/mark-read`, JSON.stringify(convId), {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              })
              .catch(() => {});
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load conversation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [contact.user_id, conversationId]);

  useEffect(() => { if (!loading) scrollToBottom(true); }, [loading]); // eslint-disable-line
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Poll
  useEffect(() => {
    if (!conversationId) return;
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/messages/${conversationId}?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newMsgs = Array.isArray(res.data) ? res.data : [];
        let changed = false;
        setMessages((prev) => {
          if (newMsgs.length !== prev.length) { changed = true; return newMsgs; }
          if (newMsgs.length > 0 && prev.length > 0 && newMsgs[newMsgs.length - 1]?.id !== prev[prev.length - 1]?.id) { changed = true; return newMsgs; }
          return prev;
        });
        // Only mark-read when new messages arrive, debounced
        const now = Date.now();
        if (changed && now - lastMarkReadRef.current > 3000) {
          lastMarkReadRef.current = now;
          axios.patch(`${API}/messages/mark-read`, JSON.stringify(conversationId), {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }).catch(() => {});
        }
      } catch { /* silent */ }
    };
    pollRef.current = setInterval(poll, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [conversationId]);

  const handleSend = async () => {
    const text = inputText.trim();
    const hasImage = !!pendingImage;
    if (!text && !hasImage) return;
    if (sending || !conversationId) return;

    // Upload image first if present
    let imageUrl = null;
    if (hasImage) {
      imageUrl = await uploadImage(pendingImage);
      if (!imageUrl) return; // upload failed
    }

    const tempMsg = {
      id: `temp-${Date.now()}`,
      sender_id: user.id,
      content: text,
      image_url: imageUrl,
      created_at: new Date().toISOString(),
      reactions: [],
      _sending: true,
    };
    setMessages((prev) => [...prev, tempMsg]);
    setInputText('');
    setPendingImage(null);
    setImagePreview(null);
    setSending(true);
    shouldAutoScroll.current = true;

    try {
      const token = localStorage.getItem('token');
      const payload = { conversation_id: conversationId, content: text };
      if (imageUrl) payload.image_url = imageUrl;
      const res = await axios.post(`${API}/messages/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === tempMsg.id ? { ...res.data, reactions: [], _sending: false } : m)),
      );
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleEndConversation = async () => {
    if (!conversationId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/conversations/${conversationId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Conversation ended');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end conversation');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const displayName = contact.display_name || contact.handle || 'Creator';
  const avatarLetter = displayName[0].toUpperCase();

  // Compute the last outgoing message ID for read receipt display
  const lastOutgoingMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender_id === user?.id) return messages[i].id;
    }
    return null;
  }, [messages, user?.id]);

  if (minimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 cursor-pointer"
        onClick={() => setMinimized(false)}
        data-testid="floating-chat-minimized"
      >
        <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-600 rounded-full px-4 py-2.5 shadow-xl hover:bg-zinc-700 transition-colors">
          <Avatar className="h-7 w-7">
            {contact.avatar_url && <AvatarImage src={contact.avatar_url} alt={displayName} />}
            <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-[10px] font-bold">{avatarLetter}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-white">{displayName}</span>
          <ChevronDown className="w-3.5 h-3.5 text-zinc-400 rotate-180" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[340px] h-[520px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col overflow-hidden"
      data-testid="floating-chat-window"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-zinc-700 bg-zinc-800/90 flex-shrink-0">
        <Avatar className="h-8 w-8 flex-shrink-0">
          {contact.avatar_url && <AvatarImage src={contact.avatar_url} alt={displayName} />}
          <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-xs font-bold">{avatarLetter}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{displayName}</p>
          {contact.handle && <p className="text-[10px] text-zinc-500">@{contact.handle}</p>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 text-zinc-400 hover:text-white transition-colors rounded-md hover:bg-zinc-700" data-testid="floating-chat-menu">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} className="bg-zinc-900 border-zinc-700 min-w-[160px]">
            {contact.handle && (
              <DropdownMenuItem onClick={() => navigate(`/u/${contact.handle}`)} className="cursor-pointer text-zinc-300 focus:text-white" data-testid="floating-view-profile">
                <User className="w-4 h-4 mr-2" />View Profile
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleEndConversation} className="cursor-pointer text-red-400 focus:text-red-300" data-testid="floating-end-convo">
              <Ban className="w-4 h-4 mr-2" />End Conversation
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onClose} className="cursor-pointer text-zinc-300 focus:text-white" data-testid="floating-close-chat">
              <X className="w-4 h-4 mr-2" />Close Chat
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={() => setMinimized(true)} className="p-1 text-zinc-400 hover:text-white transition-colors" title="Minimize" data-testid="chat-minimize-btn">
          <ChevronDown className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="p-1 text-zinc-400 hover:text-white transition-colors" title="Close" data-testid="chat-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-2" onScroll={handleScroll} data-testid="chat-messages-area">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Avatar className="h-14 w-14 mb-3">
              {contact.avatar_url && <AvatarImage src={contact.avatar_url} alt={displayName} />}
              <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-lg font-bold">{avatarLetter}</AvatarFallback>
            </Avatar>
            <p className="text-sm text-zinc-400">Start a conversation with</p>
            <p className="text-sm font-semibold text-white">{displayName}</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMine = msg.sender_id === user?.id;
            const prevMsg = messages[i - 1];
            const compact = prevMsg && prevMsg.sender_id === msg.sender_id;
            return (
              <ChatBubble
                key={msg.id}
                message={msg}
                isMine={isMine}
                senderAvatar={!isMine ? contact.avatar_url : null}
                senderName={!isMine ? displayName : null}
                compact={compact}
                reactions={msg.reactions || []}
                onReact={handleReaction}
                isLastOutgoing={msg.id === lastOutgoingMsgId}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInputToolbar
        inputText={inputText}
        onInputChange={setInputText}
        onSend={handleSend}
        onKeyDown={handleKeyDown}
        sending={sending}
        disabled={loading || !conversationId}
        inputRef={inputRef}
        onImageSelect={handleImageSelect}
        imagePreview={imagePreview}
        onImageRemove={handleImageRemove}
        imageUploading={imageUploading}
        data-testid-input="chat-message-input"
        data-testid-send="chat-send-btn"
      />
    </div>
  );
};

export default FloatingChatWindow;
